import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FiSearch, FiX } from "react-icons/fi";
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

export default function LocationPickerModal({ isOpen, onClose, onSave, title }) {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [mapSearchQuery, setMapSearchQuery] = useState('');
  const [mapSuggestions, setMapSuggestions] = useState([]);
  const [isSearchingMap, setIsSearchingMap] = useState(false);

  useEffect(() => {
    if (isOpen && mapRef.current) {
      const map = L.map(mapRef.current).setView([39.50, -105.46], 4); 
      mapInstanceRef.current = map;
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap',
      }).addTo(map);

      const onMapClick = (e) => {
        if (markerRef.current) {
          map.removeLayer(markerRef.current);
        }
        markerRef.current = L.marker([e.latlng.lat, e.latlng.lng]).addTo(map);
      };

      map.on('click', onMapClick);

      return () => {
        map.off('click', onMapClick);
        map.remove();
        mapInstanceRef.current = null;
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (mapSearchQuery.trim().length > 2) {
        setIsSearchingMap(true);
        try {
          const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(mapSearchQuery)}&limit=5`);
          const data = await response.json();
          setMapSuggestions(data || []);
        } catch (error) {
          setMapSuggestions([]);
        } finally {
          setIsSearchingMap(false);
        }
      } else {
        setMapSuggestions([]);
      }
    }, 600);
    return () => clearTimeout(delayDebounceFn);
  }, [mapSearchQuery]);

  const handleSelectMapSuggestion = (suggestion) => {
    setMapSearchQuery(suggestion.display_name);
    setMapSuggestions([]);
    
    const newLat = parseFloat(suggestion.lat);
    const newLng = parseFloat(suggestion.lon);
    
    if (mapInstanceRef.current) {
       mapInstanceRef.current.setView([newLat, newLng], 13);
       if (markerRef.current) {
         mapInstanceRef.current.removeLayer(markerRef.current);
       }
       markerRef.current = L.marker([newLat, newLng]).addTo(mapInstanceRef.current);
       markerRef.current.bindPopup(suggestion.display_name).openPopup();
    }
  };

  const handleMapSearchSubmit = async () => {
    if (!mapSearchQuery) return;
    if (mapSuggestions.length > 0) {
      handleSelectMapSuggestion(mapSuggestions[0]);
    } else {
      setIsSearchingMap(true);
      try {
        const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(mapSearchQuery)}`);
        const data = await response.json();
        if (data && data.length > 0) {
          handleSelectMapSuggestion(data[0]);
        } else {
          toast.error("Location not found");
        }
      } catch (error) {
        toast.error("Failed to search location");
      } finally {
        setIsSearchingMap(false);
      }
    }
  };

  const saveMapCoordinates = async () => {
    if (markerRef.current) {
      const { lat, lng } = markerRef.current.getLatLng();
      let addressStr = '';
      try {
        const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
        const data = await response.json();
        addressStr = data.display_name || '';
      } catch (error) {}
      
      onSave({ lat, lng, address: addressStr });
      onClose();
    } else {
      toast.error("Please select a location on the map");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-[9999] flex items-center justify-center p-4">
      <div
        className="rounded-xl shadow-2xl text-white p-4 sm:p-6 relative w-full max-w-[500px]"
        style={{ background: 'radial-gradient(50% 49.39% at 50% 50%, #1D2461 0%, #080625 100%)' }}
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-2xl text-gray-400 hover:text-white z-10">
          <FiX />
        </button>
        <h2 className="text-lg font-bold mb-4">{title || 'Select Location'}</h2>
        
        <div className="flex mb-4 gap-2 relative">
          <input
             type="text"
             placeholder="Search Location"
             value={mapSearchQuery}
             onChange={(e) => setMapSearchQuery(e.target.value)}
             onKeyDown={(e) => e.key === 'Enter' && handleMapSearchSubmit()}
             className="flex-1 px-3 py-2 rounded bg-white text-black outline-none text-sm"
          />
          <button
             onClick={handleMapSearchSubmit}
             className="bg-[#6F1AFF] hover:bg-[#5C10E6] px-4 py-2 rounded text-white text-sm flex items-center justify-center"
          >
            {isSearchingMap ? <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span> : <FiSearch />}
          </button>

          {mapSuggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white text-black rounded-lg shadow-xl border border-gray-100 max-h-60 overflow-y-auto z-[10000]">
              {mapSuggestions.map((suggestion, index) => (
                <div 
                  key={index} 
                  className="px-3 py-2 text-sm hover:bg-violet-50 cursor-pointer border-b last:border-b-0 border-gray-100"
                  onClick={() => handleSelectMapSuggestion(suggestion)}
                >
                  {suggestion.display_name}
                </div>
              ))}
            </div>
          )}
        </div>

        <div ref={mapRef} style={{ height: '300px', width: '100%', zIndex: 1, borderRadius: '8px' }}></div>
        <div className="flex justify-center mt-4">
          <button
            onClick={saveMapCoordinates}
            type="button"
            className="w-full py-2.5 bg-[#7c3aed] text-white rounded-lg font-bold shadow-lg transition-all hover:bg-[#6d28d9]"
          >
            Save Location
          </button>
        </div>
      </div>
    </div>
  );
}
